import { useCallback } from 'react';
import { ref, onValue, set, push, get, serverTimestamp } from 'firebase/database';
import { database } from '../../../config/firebase';
import useQuizValidation from './useQuizValidation';

/**
 * クイズ関連のFirebase操作を担当する共通フック
 * 既存のuseQuizPlayer.jsとQuizSection.jsからFirebase操作を抽出・統一
 */
const useQuizFirebase = () => {
  const { validateFirebaseData, validateDetailedResults } = useQuizValidation();

  /**
   * クイズ結果をFirebaseに保存
   * @param {Object} params - 保存パラメータ
   * @param {string} params.studentId - 学生ID
   * @param {string} params.modalContentId - コンテンツID
   * @param {Array} params.questions - 問題配列
   * @param {Object} params.responses - 回答オブジェクト
   * @param {number} params.startTime - 開始時間
   * @param {boolean} params.forced - 強制終了フラグ
   * @returns {Promise<Object>} - 保存結果
   */
  const saveQuizResults = useCallback(async ({
    studentId,
    modalContentId,
    questions,
    responses,
    startTime,
    forced = false
  }) => {
    try {
      const total = questions.length;
      const correctCount = Object.values(responses).filter((r) => r.isCorrect).length;
      const correctRate = total > 0 ? (correctCount / total) * 100 : 0;
      const currentEndTime = Date.now();
      const elapsedTime = startTime ? currentEndTime - startTime : 0;

      // 詳細結果の生成と検証
      const detailedResults = validateDetailedResults(questions, responses);

      // 既存履歴の取得
      const resultsRef = ref(database, `results/${studentId}/${modalContentId}`);
      const snapshot = await get(resultsRef);
      const data = snapshot.val();
      const history = data ? Object.values(data) : [];
      const currentAttemptNumber = history.length + 1;

      // 保存データの構築
      const resultData = {
        attemptNumber: currentAttemptNumber,
        timestamp: serverTimestamp(),
        correctRate,
        correctCount,
        total,
        elapsedTime,
        detailedResults,
        isTimeOver: forced,
      };

      // Firebase保存用データの検証
      const safeResultData = validateFirebaseData(resultData);

      // Firebase保存実行
      const resultRef = push(resultsRef);
      await set(resultRef, safeResultData);

      return {
        success: true,
        correctRate,
        correctCount,
        total,
        attemptNumber: currentAttemptNumber,
        elapsedTime
      };

    } catch (error) {
      console.error('🔴 useQuizFirebase - 結果の保存に失敗しました:', error);
      return {
        success: false,
        message: '結果の保存に失敗しました。',
        error: error.message
      };
    }
  }, [validateFirebaseData, validateDetailedResults]);

  /**
   * 学生の結果履歴を取得
   * @param {string} studentId - 学生ID
   * @param {string} contentId - コンテンツID
   * @returns {Promise<Array>} - 結果履歴配列
   */
  const getStudentResults = useCallback(async (studentId, contentId) => {
    try {
      const resultsRef = ref(database, `results/${studentId}/${contentId}`);
      const snapshot = await get(resultsRef);
      const data = snapshot.val();
      
      if (!data) {
        return [];
      }

      // 結果をタイムスタンプ順でソート
      const results = Object.values(data).sort((a, b) => 
        (a.timestamp || 0) - (b.timestamp || 0)
      );

      return results;
    } catch (error) {
      console.error('🔴 useQuizFirebase - 結果の取得に失敗しました:', error);
      throw new Error('結果の取得に失敗しました。');
    }
  }, []);

  /**
   * コンテンツのクイズデータを取得
   * @param {string} contentId - コンテンツID
   * @returns {Promise<Object>} - コンテンツデータ
   */
  const getContentData = useCallback(async (contentId) => {
    try {
      const contentRef = ref(database, `contents/${contentId}`);
      const snapshot = await get(contentRef);
      const content = snapshot.val();
      
      if (!content) {
        throw new Error(`コンテンツID ${contentId} が見つかりません`);
      }

      return content;
    } catch (error) {
      console.error('🔴 useQuizFirebase - コンテンツの取得に失敗しました:', error);
      throw new Error('コンテンツの取得に失敗しました。');
    }
  }, []);

  /**
   * リアルタイムでコンテンツデータを監視
   * @param {string} contentId - コンテンツID
   * @param {Function} callback - データ変更時のコールバック
   * @returns {Function} - 監視停止関数
   */
  const watchContentData = useCallback((contentId, callback) => {
    if (!contentId) {
      callback(null);
      return () => {};
    }

    const contentRef = ref(database, `contents/${contentId}`);
    const unsubscribe = onValue(contentRef, (snapshot) => {
      const content = snapshot.val();
      callback(content);
    }, (error) => {
      console.error('🔴 useQuizFirebase - コンテンツ監視エラー:', error);
      callback(null);
    });

    return unsubscribe;
  }, []);

  /**
   * 学生割り当てコンテンツの監視
   * @param {string} studentId - 学生ID
   * @param {Function} callback - データ変更時のコールバック
   * @returns {Function} - 監視停止関数
   */
  const watchStudentAssignedContents = useCallback((studentId, callback) => {
    if (!studentId) {
      callback([]);
      return () => {};
    }

    const assignedRef = ref(database, `studentAssignedContents/${studentId}`);
    const unsubscribe = onValue(assignedRef, (snapshot) => {
      const data = snapshot.val();
      const assignedIds = data ? Object.keys(data).filter(id => data[id] === true) : [];
      callback(assignedIds);
    }, (error) => {
      console.error('🔴 useQuizFirebase - 割り当て監視エラー:', error);
      callback([]);
    });

    return unsubscribe;
  }, []);

  /**
   * 全コンテンツの監視（教師用）
   * @param {Function} callback - データ変更時のコールバック
   * @returns {Function} - 監視停止関数
   */
  const watchAllContents = useCallback((callback) => {
    const contentsRef = ref(database, 'contents');
    const unsubscribe = onValue(contentsRef, (snapshot) => {
      const data = snapshot.val();
      const contents = data ? Object.entries(data).map(([id, content]) => ({
        id,
        ...content
      })) : [];
      callback(contents);
    }, (error) => {
      console.error('🔴 useQuizFirebase - 全コンテンツ監視エラー:', error);
      callback([]);
    });

    return unsubscribe;
  }, []);

  return {
    // 結果関連
    saveQuizResults,
    getStudentResults,
    
    // コンテンツ関連
    getContentData,
    watchContentData,
    watchStudentAssignedContents,
    watchAllContents
  };
};

export { useQuizFirebase };
export default useQuizFirebase; 